<!--
  El directorio de la instalación: la portada en `/`.

  Lista las aplicaciones públicas y, con la sesión del directorio, las de la
  persona («Mis aplicaciones»). Cada tarjeta lleva la paleta de su aplicación
  (`paletteAttrs`), así que el color de la pantalla sale de las aplicaciones y
  no de una marca propia: el mosaico de la barra y el resplandor del fondo usan
  las primeras. Ver `app-directory` en `openspec/changes/directorio`.
-->
<script lang="ts">
  import { appPath } from "@shared/appPaths";
  import type { DirectoryApp, DirectoryMemberApp } from "@shared/types";
  import { untrack } from "svelte";

  import Icon from "../components/Icon.svelte";
  import ThemePicker from "../components/ThemePicker.svelte";
  import {
    Button,
    Dropdown,
    EmptyState,
    ErrorNote,
    Field,
    Input,
    MenuItem,
    Modal,
  } from "../components/ui";
  import { paletteAttrs } from "../lib/appTheme";
  import { directorySession } from "../lib/directorySession.svelte";
  import { errorMessage } from "../lib/pb";
  import { link } from "../lib/router.svelte";
  import { useAsync } from "../lib/useAsync.svelte";

  let { apps }: { apps: DirectoryApp[] } = $props();
  let email = $state("");
  let password = $state("");
  let busy = $state(false);
  let error = $state("");
  let loginOpen = $state(false);
  let search = $state("");
  // Se valida al montar, no al entrar o salir: esas acciones ya actualizan la sesión.
  const loaded = useAsync(() => untrack(() => directorySession.refresh()));
  const ready = $derived(!loaded.loading && !loaded.error);
  const member = $derived(ready ? directorySession.apps[0]?.member : undefined);
  const account = $derived(member?.cuenta ?? "");
  const firstName = $derived((member?.name || account.split("@")[0]).split(" ")[0]);
  const initials = $derived(
    (member?.name || account)
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toLocaleUpperCase("es"),
  );
  const publicApps = $derived(
    apps.filter((app) => !directorySession.apps.some((mine) => mine.id === app.id)),
  );
  const total = $derived(publicApps.length + (member ? directorySession.apps.length : 0));
  /** Las que dan color al mosaico y al resplandor: primero las de la persona. */
  const colorful = $derived([...(member ? directorySession.apps : []), ...publicApps].slice(0, 4));
  const normalize = (value: string) =>
    value.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase("es").trim();
  const query = $derived(normalize(search));
  const matches = (app: DirectoryApp) => normalize(app.name).includes(query);
  const mine = $derived(member ? directorySession.apps.filter(matches) : []);
  const publicMatches = $derived(publicApps.filter(matches));

  function openLogin() {
    error = "";
    loginOpen = true;
  }

  function closeLogin() {
    loginOpen = false;
    password = "";
    error = "";
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (busy) return;
    busy = true;
    error = "";
    try {
      await directorySession.enter(email, password);
      closeLogin();
    } catch (err) {
      error = errorMessage(err);
    } finally {
      busy = false;
    }
  }

  // Modal no usa un diálogo nativo: el foco necesita quedar dentro mientras se escribe.
  function focusLogin(form: HTMLFormElement) {
    const dialog = form.closest<HTMLElement>('[role="dialog"]');
    // `Modal` pone el foco en su panel al abrirse; el correo lo recibe un fotograma después.
    const frame = requestAnimationFrame(() =>
      form.querySelector<HTMLInputElement>('input[type="email"]')?.focus(),
    );
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !dialog) return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), a[href]",
        ),
      ).filter(
        (node) => node.getClientRects().length && !node.classList.contains("plane-backdrop"),
      );
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    dialog?.addEventListener("keydown", trap);
    return {
      destroy() {
        cancelAnimationFrame(frame);
        dialog?.removeEventListener("keydown", trap);
        document.getElementById("btn-directory-session")?.focus();
      },
    };
  }
</script>

{#snippet appCard(app: DirectoryApp, memberApp?: DirectoryMemberApp)}
  <a
    class="card-directory-app"
    href={appPath(app.slug)}
    use:link
    onclick={() => {
      if (memberApp) directorySession.open(memberApp);
    }}
    {...paletteAttrs(app.theme, { fontScale: false })}
  >
    <span class="tile-directory-app" aria-hidden="true"><Icon name={app.icon} size={22} /></span>
    <span class="text-directory-app">
      <span class="name-directory-app">{app.name}</span>
      <span class="meta-directory-app">
        {#if app.visibility === "public"}
          <Icon name="globe-02" size={13} /> Pública
        {:else}
          <Icon name="lock-key" size={13} /> Con inicio de sesión
        {/if}
      </span>
    </span>
    <span class="arrow-directory-app" aria-hidden="true">
      <Icon name="arrow-right-01" size={18} />
    </span>
  </a>
{/snippet}

<div class="page-directory">
  <!-- El resplandor se hace con el acento de las primeras aplicaciones. -->
  <div class="glow-directory" aria-hidden="true">
    {#each colorful as app (app.id)}
      <span class="blob-directory" {...paletteAttrs(app.theme, { fontScale: false })}></span>
    {/each}
  </div>

  <header class="header-directory">
    <div class="bar-directory">
      <a class="brand-directory" href="/" use:link>
        <span class="mark-directory" aria-hidden="true">
          {#each [0, 1, 2, 3] as slot (slot)}
            {@const app = colorful[slot % Math.max(colorful.length, 1)]}
            <span {...app ? paletteAttrs(app.theme, { fontScale: false }) : {}}></span>
          {/each}
        </span>
        Aplicaciones
      </a>
      <div class="actions-directory">
        <ThemePicker class="btn-directory-theme" />
        {#if member}
          <Dropdown align="right" class="menu-directory-session">
            {#snippet trigger({ open, toggle })}
              <button
                id="btn-directory-session"
                type="button"
                class="btn-directory-account"
                class:active={open}
                aria-label="Sesión de {account}"
                aria-expanded={open}
                onclick={toggle}
              >
                <span class="avatar-directory" aria-hidden="true">{initials}</span>
                <span class="name-directory-account">{firstName}</span>
                <Icon name="arrow-down-01" size={14} />
              </button>
            {/snippet}
            {#snippet children(close)}
              <div class="identity-directory-session">
                <strong>{member?.name || account}</strong>
                <span>{account}</span>
              </div>
              <MenuItem
                class="btn-directory-logout"
                onclick={() => {
                  close();
                  search = "";
                  directorySession.clear();
                }}
              >
                {#snippet icon()}<Icon name="logout-01" />{/snippet}
                Cerrar sesión
              </MenuItem>
            {/snippet}
          </Dropdown>
        {:else if ready}
          <Button
            id="btn-directory-session"
            buttonClass="btn-directory-login"
            size="sm"
            onclick={openLogin}
          >
            Iniciar sesión
          </Button>
        {/if}
      </div>
    </div>
  </header>

  <main class="main-directory">
    <section class="hero-directory" aria-labelledby="title-directory">
      <p class="eyebrow-directory">
        {#if member}
          Hola, {firstName}
        {:else}
          {apps.length}
          {apps.length === 1 ? "aplicación disponible" : "aplicaciones disponibles"}
        {/if}
      </p>
      <h1 id="title-directory" class="title-directory">
        {member ? "Tus aplicaciones" : "Elige una aplicación"}
      </h1>
      <p class="support-directory">
        {member
          ? "Las que tienes a mano con tu cuenta y las públicas de esta instalación."
          : "Abre una aplicación pública o inicia sesión para ver las tuyas."}
      </p>
      {#if total > 4 && ready}
        <label class="search-directory">
          <Icon name="search-01" size={18} />
          <input
            class="input-directory-search"
            type="search"
            aria-label="Buscar aplicaciones por nombre"
            placeholder="Buscar aplicaciones"
            bind:value={search}
          />
        </label>
      {/if}
    </section>

    {#if loaded.loading}
      <ul class="list-directory-skeleton list-directory-apps" aria-label="Cargando aplicaciones">
        {#each [1, 2, 3, 4, 5, 6] as item (item)}
          <li class="card-directory-skeleton">
            <span class="skeleton-directory-tile skeleton"></span>
            <span class="skeleton-directory-text">
              <span class="skeleton-directory-name skeleton"></span>
              <span class="skeleton-directory-meta skeleton"></span>
            </span>
          </li>
        {/each}
      </ul>
    {:else if loaded.error}
      <section class="error-directory-session">
        <ErrorNote message={loaded.error} />
        <Button buttonClass="btn-directory-retry" onclick={() => void loaded.reload()}>
          Reintentar
        </Button>
      </section>
    {:else}
      {#if member}
        <section class="section-directory-mine" aria-labelledby="title-directory-mine">
          <header class="head-directory-section">
            <h2 id="title-directory-mine">Mis aplicaciones</h2>
            <span class="count-directory">{mine.length}</span>
          </header>
          {#if mine.length}
            <ul class="list-directory-mine list-directory-apps">
              {#each mine as app (app.id)}
                <li>{@render appCard(app, app)}</li>
              {/each}
            </ul>
          {:else}
            <p class="none-directory">Ninguna de tus aplicaciones coincide con la búsqueda.</p>
          {/if}
          <p class="hint-directory-password">
            <Icon name="information-circle" size={14} />
            Solo se muestran las aplicaciones con la contraseña que escribiste.
          </p>
        </section>
      {:else}
        <button type="button" class="banner-directory-login" onclick={openLogin}>
          <span class="banner-directory-icon" aria-hidden="true">
            <Icon name="user-circle" size={20} />
          </span>
          <span class="banner-directory-text">
            <strong>¿Te invitaron a una aplicación?</strong>
            <span>Inicia sesión con tu correo para verlas todas aquí.</span>
          </span>
          <Icon name="arrow-right-01" size={18} />
        </button>
      {/if}

      <section class="section-directory-public" aria-labelledby="title-directory-public">
        <header class="head-directory-section">
          <h2 id="title-directory-public">Aplicaciones públicas</h2>
          <span class="count-directory" aria-live="polite">{publicMatches.length}</span>
        </header>
        {#if publicMatches.length}
          <ul class="list-directory-public list-directory-apps">
            {#each publicMatches as app (app.id)}<li>{@render appCard(app)}</li>{/each}
          </ul>
        {:else}
          <EmptyState
            title={query && publicApps.length
              ? "No hay aplicaciones públicas con ese nombre."
              : apps.length && member
                ? "Todas las aplicaciones públicas están en «Mis aplicaciones»."
                : "Todavía no hay aplicaciones públicas."}
            description={query && publicApps.length
              ? "Prueba con otro nombre o borra la búsqueda."
              : undefined}
          >
            {#snippet icon()}<Icon
                name={query ? "search-01" : "dashboard-square-01"}
                size={22}
              />{/snippet}
            {#snippet action()}
              {#if query}
                <Button buttonClass="btn-directory-clear-search" onclick={() => (search = "")}>
                  Borrar búsqueda
                </Button>
              {/if}
            {/snippet}
          </EmptyState>
        {/if}
      </section>
    {/if}
  </main>

  <footer class="footer-directory">
    <a class="link-directory-panel" href="/admin" use:link>
      <Icon name="settings-02" size={14} /> Panel de administración
    </a>
  </footer>
</div>

<Modal
  open={loginOpen}
  onClose={closeLogin}
  title="Iniciar sesión"
  description="Usa el correo y la contraseña con los que te invitaron."
  class="modal-directory-login"
>
  <form
    id="form-directory-login"
    class="form-directory-login field-stack"
    onsubmit={submit}
    use:focusLogin
  >
    <Field label="Correo">
      <Input type="email" autocomplete="username" bind:value={email} required disabled={busy} />
    </Field>
    <Field label="Contraseña">
      <Input
        type="password"
        autocomplete="current-password"
        bind:value={password}
        required
        disabled={busy}
      />
    </Field>
    <ErrorNote message={error} />
  </form>
  {#snippet footer()}
    <Button
      type="submit"
      form="form-directory-login"
      variant="secondary"
      buttonClass="btn-directory-submit"
      loading={busy}
    >
      Iniciar sesión
    </Button>
  {/snippet}
</Modal>

<style>
  .page-directory {
    position: relative;
    isolation: isolate;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow-x: clip;
    background: var(--bg-level1);
    color: var(--text-primary);
  }

  /* Tres o cuatro manchas difuminadas, una por aplicación, detrás de la cabecera. */
  .glow-directory {
    position: absolute;
    inset: 0 0 auto;
    height: 36rem;
    z-index: -1;
    pointer-events: none;
    mask-image: linear-gradient(to bottom, black 30%, transparent);
  }
  .blob-directory {
    position: absolute;
    width: 28rem;
    aspect-ratio: 1;
    border-radius: var(--radius-full);
    background: var(--accent);
    opacity: 0.16;
    filter: blur(6rem);
    &:nth-child(1) {
      top: -12rem;
      left: -6rem;
    }
    &:nth-child(2) {
      top: -14rem;
      left: 38%;
    }
    &:nth-child(3) {
      top: -10rem;
      right: -8rem;
    }
    &:nth-child(4) {
      top: 4rem;
      left: 18%;
      opacity: 0.08;
    }
  }

  .header-directory {
    position: sticky;
    top: 0;
    z-index: 2;
    background: color-mix(in oklch, var(--bg-level1) 72%, transparent);
    backdrop-filter: blur(var(--sp-16)) saturate(1.4);
    border-bottom: var(--border-width) solid color-mix(in oklch, var(--border) 60%, transparent);
  }
  .bar-directory,
  .main-directory,
  .footer-directory {
    width: 100%;
    max-width: 72rem;
    margin-inline: auto;
    padding-inline: var(--space-8);
    @media (width < 40rem) {
      padding-inline: var(--sp-16);
    }
  }
  .bar-directory {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-12);
    height: 3.75rem;
  }
  .brand-directory {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-10);
    color: var(--text-primary);
    font-weight: 600;
    font-size: var(--type-base);
    letter-spacing: -0.01em;
  }
  /* El mosaico de la marca: un cuadrado de cuatro, con el color de cada aplicación. */
  .mark-directory {
    display: grid;
    grid-template-columns: repeat(2, var(--sp-10));
    gap: 2px;
    & > span {
      aspect-ratio: 1;
      border-radius: 3px;
      background: var(--accent);
    }
    & > span:nth-child(2),
    & > span:nth-child(3) {
      opacity: 0.85;
    }
  }
  .actions-directory {
    display: flex;
    align-items: center;
    gap: var(--sp-8);
  }
  .btn-directory-account {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-8);
    height: 2.25rem;
    padding: 0 var(--sp-10) 0 var(--sp-4);
    border-radius: var(--radius-full);
    border: var(--border-width) solid var(--border);
    background: var(--bg-level2);
    color: var(--text-secondary);
    font-size: var(--type-sm);
    transition: border-color var(--mo-t-press) var(--mo-move);
    &:hover,
    &.active {
      border-color: var(--border-strong);
      color: var(--text-primary);
    }
    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
    @media (width < 40rem) {
      & .name-directory-account {
        display: none;
      }
    }
  }
  .avatar-directory {
    display: grid;
    place-items: center;
    width: 1.75rem;
    aspect-ratio: 1;
    border-radius: var(--radius-full);
    background: var(--accent);
    color: var(--accent-text);
    font-size: var(--type-xs);
    font-weight: 600;
  }
  .identity-directory-session {
    display: grid;
    gap: 2px;
    max-width: 18rem;
    padding: var(--sp-10) var(--sp-12);
    margin-bottom: var(--sp-4);
    border-bottom: var(--border-width) solid var(--border);
    font-size: var(--type-sm);
    overflow-wrap: anywhere;
    & span {
      color: var(--text-secondary);
      font-size: var(--type-xs);
    }
  }

  .main-directory {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-10);
    padding-block: var(--space-12) var(--space-12);
    @media (width < 40rem) {
      gap: var(--space-8);
      padding-block: var(--space-8);
    }
  }
  .hero-directory {
    display: grid;
    justify-items: start;
    gap: var(--sp-12);
    max-width: 44rem;
  }
  .eyebrow-directory {
    color: var(--accent);
    font-size: var(--type-sm);
    font-weight: 600;
  }
  .title-directory {
    font-size: clamp(var(--type-3xl), 6vw, calc(var(--type-3xl) * 1.6));
    line-height: 1.05;
    font-weight: 650;
    letter-spacing: -0.035em;
    text-wrap: balance;
  }
  .support-directory {
    color: var(--text-secondary);
    font-size: var(--type-lg);
    text-wrap: pretty;
    @media (width < 40rem) {
      font-size: var(--type-base);
    }
  }
  .search-directory {
    display: flex;
    align-items: center;
    gap: var(--sp-10);
    width: min(100%, 28rem);
    height: 3rem;
    margin-top: var(--sp-12);
    padding-inline: var(--sp-16);
    border-radius: var(--radius-full);
    border: var(--border-width) solid var(--border);
    background: var(--bg-level2);
    box-shadow: var(--shadow-sm);
    color: var(--text-muted);
    cursor: text;
    transition:
      border-color var(--mo-t-press) var(--mo-move),
      box-shadow var(--mo-t-press) var(--mo-move);
    &:focus-within {
      border-color: var(--accent);
      box-shadow: 0 0 0 3px var(--accent-soft);
    }
    & .input-directory-search {
      flex: 1;
      min-width: 0;
      height: 100%;
      background: none;
      border: 0;
      outline: 0;
      color: var(--text-primary);
      font: inherit;
      &::placeholder {
        color: var(--text-muted);
      }
    }
  }

  .banner-directory-login {
    display: flex;
    align-items: center;
    gap: var(--sp-14);
    width: 100%;
    padding: var(--sp-16) var(--sp-20);
    border-radius: var(--radius-xl);
    border: var(--border-width) dashed var(--border-strong);
    background: color-mix(in oklch, var(--bg-level2) 70%, transparent);
    color: var(--text-secondary);
    text-align: left;
    transition:
      border-color var(--mo-t-press) var(--mo-move),
      background var(--mo-t-press) var(--mo-move);
    &:hover {
      border-color: var(--accent);
      border-style: solid;
      background: var(--bg-level2);
      color: var(--text-primary);
    }
    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
  }
  .banner-directory-icon {
    display: grid;
    place-items: center;
    width: 2.5rem;
    aspect-ratio: 1;
    border-radius: var(--radius-full);
    background: var(--accent-soft);
    color: var(--accent-soft-text);
  }
  .banner-directory-text {
    flex: 1;
    display: grid;
    gap: 2px;
    font-size: var(--type-sm);
    & strong {
      color: var(--text-primary);
      font-weight: 600;
    }
  }

  .head-directory-section {
    display: flex;
    align-items: baseline;
    gap: var(--sp-10);
    margin-bottom: var(--sp-16);
    & h2 {
      font-size: var(--type-sm);
      font-weight: 600;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }
  }
  .count-directory {
    padding: 0 var(--sp-8);
    border-radius: var(--radius-full);
    background: var(--bg-field);
    color: var(--text-secondary);
    font-size: var(--type-xs);
    font-variant-numeric: tabular-nums;
  }
  .list-directory-apps {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 17rem), 1fr));
    gap: var(--sp-12);
    & > li {
      min-width: 0;
    }
  }

  .card-directory-app {
    display: flex;
    align-items: center;
    gap: var(--sp-14);
    height: 100%;
    padding: var(--sp-14);
    border-radius: var(--radius-xl);
    border: var(--border-width) solid var(--border);
    background: var(--bg-level2);
    color: var(--text-primary);
    transition:
      transform var(--mo-t-press) var(--mo-enter),
      border-color var(--mo-t-press) var(--mo-move),
      box-shadow var(--mo-t-press) var(--mo-move),
      background var(--mo-t-press) var(--mo-move);
    &:hover {
      border-color: color-mix(in oklch, var(--accent) 50%, var(--border));
      background: linear-gradient(
        100deg,
        color-mix(in oklch, var(--accent-soft) 70%, var(--bg-level2)),
        var(--bg-level2) 60%
      );
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);
      & .arrow-directory-app {
        color: var(--accent);
        transform: translateX(2px);
      }
    }
    &:active {
      transform: translateY(0);
    }
    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) {
      &,
      & .arrow-directory-app {
        transition: none;
      }
      &:hover,
      &:hover .arrow-directory-app {
        transform: none;
      }
    }
  }
  .tile-directory-app {
    display: grid;
    place-items: center;
    width: 2.875rem;
    aspect-ratio: 1;
    border-radius: var(--radius-lg);
    background: var(--accent);
    color: var(--accent-text);
    box-shadow:
      inset 0 1px 0 oklch(from var(--accent-text) l c h / 0.22),
      0 4px 12px -4px oklch(from var(--accent) l c h / 0.6);
  }
  .text-directory-app {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 3px;
  }
  .name-directory-app {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .meta-directory-app {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-4);
    color: var(--text-muted);
    font-size: var(--type-xs);
  }
  .arrow-directory-app {
    display: grid;
    color: var(--text-muted);
    transition:
      color var(--mo-t-press) var(--mo-move),
      transform var(--mo-t-press) var(--mo-enter);
  }

  .none-directory {
    color: var(--text-secondary);
    font-size: var(--type-sm);
  }
  .hint-directory-password {
    display: flex;
    align-items: center;
    gap: var(--sp-6);
    margin-top: var(--sp-12);
    color: var(--text-muted);
    font-size: var(--type-xs);
  }

  .card-directory-skeleton {
    display: flex;
    align-items: center;
    gap: var(--sp-14);
    padding: var(--sp-14);
    border-radius: var(--radius-xl);
    border: var(--border-width) solid var(--border);
    background: var(--bg-level2);
  }
  .skeleton-directory-tile {
    width: 2.875rem;
    aspect-ratio: 1;
    border-radius: var(--radius-lg);
  }
  .skeleton-directory-text {
    flex: 1;
    display: grid;
    gap: var(--sp-8);
  }
  .skeleton-directory-name {
    width: 70%;
    height: var(--sp-12);
  }
  .skeleton-directory-meta {
    width: 40%;
    height: var(--sp-8);
  }
  .error-directory-session {
    display: grid;
    justify-items: start;
    gap: var(--sp-16);
  }

  .footer-directory {
    padding-block: var(--sp-24) var(--space-8);
    border-top: var(--border-width) solid var(--border);
  }
  .link-directory-panel {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-6);
    min-height: 2.75rem;
    color: var(--text-muted);
    font-size: var(--type-sm);
    &:hover {
      color: var(--text-primary);
    }
  }
</style>
